import { ComponentType } from 'react';
import i18n from '@/i18n';
import { SaveAsSetupPackageDialog } from '@/finance/SaveAsSetupPackageDialog';

/**
 * Bespoke (non-DSL) items in the Save button's dropdown on an entity's generated DetailView,
 * keyed by entity name. The generated views render `<SaveMenuButton entity="X" …/>` in place of
 * the plain Save button; with no registered (and permitted) items it renders the plain button
 * unchanged, so every entity pays nothing until it opts in.
 *
 * Fourth sibling of the extras registries:
 *   - menuActionExtras   → the row's Actions menu, on the DETAIL view
 *   - detailViewExtras   → banners/panels, on the DETAIL view
 *   - listViewExtras     → toolbar actions, on the LIST view
 *   - saveMenuExtras     → the Save button's dropdown, on the DETAIL view  ← this one
 *
 * The Save dropdown is for "capture what this page holds" actions (save as template, save setup
 * as package) — things a user reaches for at the moment of saving, as opposed to the Actions
 * menu's operations ON the record. Items require a SAVED record (they receive its id) and are
 * hidden on the create view. Entities with `templates: enabled` currently render
 * SaveTemplateSplitButton instead; unify the two the day one entity needs both.
 */

export interface SaveMenuExtraDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

export interface SaveMenuExtra {
  id: string;
  label: string;
  /** Translation key for the menu item text; `label` is the English fallback. */
  labelKey?: string;
  /** Optional permission; the item is hidden when the user lacks it (the server still enforces). */
  permission?: string;
  Dialog: ComponentType<SaveMenuExtraDialogProps>;
}

const registry: Record<string, SaveMenuExtra[]> = {
  PlanScenario: [
    {
      // The scenario's setup closure as a reusable/transferable Setup Package
      // (spec: docs/15-designspecs/Setup-Packages.md).
      id: 'save-as-setup-package',
      label: 'Save setup as package…',
      labelKey: 'Finance.SaveAsSetupPackage.Title',
      permission: 'Core.SetupPackage.Create',
      Dialog: ({ entityId, open, onClose }) => (
        <SaveAsSetupPackageDialog entityId={entityId} open={open} onClose={onClose} />
      ),
    },
  ],
};

export function getSaveMenuExtras(entity: string): SaveMenuExtra[] {
  return (registry[entity] ?? []).map((extra) => ({
    ...extra,
    label: extra.labelKey ? i18n.t(extra.labelKey, { defaultValue: extra.label }) : extra.label,
  }));
}
